import type { RefObject } from "react";
import { createContext, useContext, useMemo, useRef } from "react";
import get from "lodash/get";
import set from "lodash/set";
import unset from "lodash/unset";
import { invariant } from "@searchui-searchforms/shared/utils";
import type { FieldRefsContextType } from "../types";

export type FieldRefs = {
    [key: string]: FieldRefs | HTMLElement;
};

export const FieldRefsContext = createContext<FieldRefsContextType | null>(null);

export const useFieldRefsAPI = () => {
    const fieldRefsRef = useRef<FieldRefs>({});

    return useMemo<FieldRefsContextType>(
        () => ({
            getFieldRef: <T extends HTMLElement = HTMLElement>(path: string): T | undefined =>
                get(fieldRefsRef.current, path) as T | undefined,

            registerFieldRef: (path: string, ref: RefObject<HTMLElement>) => {
                set(fieldRefsRef.current, path, ref);
            },
            unregisterFieldRef: (path: string) => {
                unset(fieldRefsRef.current, path);
            },
        }),
        [],
    );
};

export const useFieldRefsContext = () => {
    const context = useContext(FieldRefsContext);

    invariant(context, "useFieldRefsContext must be used inside a context provider");

    return context;
};
